import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { T } from '../../theme/tokens';

type BadgeStatus = 'green' | 'yellow' | 'red' | 'blue' | 'neutral' | 'magenta';
type BadgeSize   = 'xs' | 'sm' | 'md';

interface SynBadgeProps {
    status:   BadgeStatus;
    label:    string;
    dot?:     boolean;
    size?:    BadgeSize;
    sx?:      SxProps<Theme>;
}

const STATUS_MAP: Record<BadgeStatus, { bg: string; border: string; color: string }> = {
    green:   { bg: T.color.status.greenBg,  border: T.color.status.greenBdr,  color: T.color.status.green },
    yellow:  { bg: T.color.status.amberBg,  border: T.color.status.amberBdr,  color: T.color.status.amber },
    red:     { bg: T.color.status.redBg,    border: T.color.status.redBdr,    color: T.color.status.red },
    blue:    { bg: T.color.status.blueBg,   border: T.color.status.blueBdr,   color: T.color.status.blue },
    neutral: { bg: 'rgba(10,42,56,0.06)',   border: T.color.border.medium,    color: T.color.text.secondary },
    magenta: { bg: 'rgba(155,63,191,0.10)', border: 'rgba(155,63,191,0.25)',  color: T.color.magenta[500] },
};

const SIZE_MAP: Record<BadgeSize, { fontSize: string; px: number; py: number; dotSize: number }> = {
    xs: { fontSize: '0.60rem', px: 6,  py: 2,  dotSize: 5 },
    sm: { fontSize: '0.70rem', px: 8,  py: 3,  dotSize: 6 },
    md: { fontSize: '0.78rem', px: 10, py: 4,  dotSize: 7 },
};

export function SynBadge({ status, label, dot = true, size = 'sm', sx }: SynBadgeProps) {
    const s = STATUS_MAP[status];
    const z = SIZE_MAP[size];

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                px: `${z.px}px`,
                py: `${z.py}px`,
                borderRadius: `${T.radius.full}px`,
                background: s.bg,
                border: `1px solid ${s.border}`,
                color: s.color,
                fontSize: z.fontSize,
                fontWeight: 600,
                letterSpacing: '0.03em',
                whiteSpace: 'nowrap',
                ...(sx as object),
            }}
        >
            {dot && (
                <Box component="span" sx={{
                    width: z.dotSize, height: z.dotSize,
                    borderRadius: '50%',
                    background: s.color,
                    flexShrink: 0,
                    boxShadow: `0 0 6px ${s.color}66`,
                }} />
            )}
            {label}
        </Box>
    );
}
